import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./News.module.css";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Loader from "../components/Loader";
import { useBuilding } from "../context/BuildingContext";
import { tolerantId } from "../utils/firestoreHelpers";
import { FaBullhorn } from "react-icons/fa";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  AppEmptyState,
  SectionLabel,
  IconBox,
} from "../components/app";
import { appColors, appType, appSpacing } from "../utils/appTheme";
import { formatDateLocalized } from "../utils/formatters";
import { getLocaleTag } from "../i18n";
import { useLanguage } from "../context/LanguageContext";
import { translateItems } from "../utils/translateContent";
import ComunicacionesResidente from "../components/ComunicacionesResidente";

const News = ({ setSelected, goBack } = {}) => {
  const { t } = useTranslation("novedades");
  const { language } = useLanguage();
  const { selectedBuilding } = useBuilding();
  const isMobile = useIsMobile();
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  // Traducción on-view del contenido dinámico (lo escribe el admin en español).
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    const fetchNews = async () => {
      if (!selectedBuilding?.buildingid) return;

      setLoading(true);
      try {
        const newsQuery = query(
          collection(db, "novedades"),
          where("buildingid", "in", tolerantId(selectedBuilding.buildingid))
        );
        const snapshot = await getDocs(newsQuery);
        // Los borradores del admin (published: false) no se muestran; las viejas
        // sin el campo y las del cierre de mes (published: true) sí.
        const list = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((n) => n.published !== false);

        // BUG-54 fix: Manejar dates que son strings o Timestamps
        list.sort((a, b) => {
          const getTime = (d) => {
            if (!d) return 0;
            if (d.seconds) return d.seconds;
            if (d.toDate) return d.toDate().getTime() / 1000;
            const parsed = new Date(d);
            return isNaN(parsed.getTime()) ? 0 : parsed.getTime() / 1000;
          };
          return getTime(b.date) - getTime(a.date);
        });
        setNewsList(list);
      } catch (error) {
        console.error("Error al traer novedades:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [selectedBuilding]);

  // Traducir las novedades al idioma del residente (es = original, sin llamada).
  useEffect(() => {
    if (language === "es" || newsList.length === 0) { setTranslations({}); return; }
    let cancelled = false;
    (async () => {
      const items = newsList.map((it) => ({
        id: it.id,
        title: it.title || "",
        body: it.description || it.detail || "",
      }));
      const tr = await translateItems(items, language);
      if (!cancelled) setTranslations(tr);
    })();
    return () => { cancelled = true; };
  }, [newsList, language]);

  // Helpers: título/cuerpo traducido con fallback al original (español).
  const trTitle = (it) => translations[it.id]?.title || it.title;
  const trBody = (it) => translations[it.id]?.body || it.description || it.detail || "";

  // ─── Versión mobile: estilo app "Radial" (screen 09 · Novedades) ────────────
  if (isMobile) {
    return (
      <AppScreen
        header={
          <AppHeader
            title={t("title")}
            subtitle={t("subtitle")}
            onBack={goBack}
          />
        }
      >
        <ComunicacionesResidente buildingId={selectedBuilding?.buildingid} />
        {!selectedBuilding?.buildingid ? (
          <AppEmptyState
            icon={<FaBullhorn />}
            title={t("selectBuilding.title")}
            description={t("selectBuilding.description")}
          />
        ) : loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <Loader />
          </div>
        ) : newsList.length === 0 ? (
          <AppEmptyState
            icon={<FaBullhorn />}
            title={t("empty.title")}
            description={t("empty.description")}
          />
        ) : (
          <>
            <SectionLabel
              style={{ marginBottom: 12 }}
              action={{
                label: t("count", { count: newsList.length }),
              }}
            >
              {t("sectionTitle")}
            </SectionLabel>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: appSpacing.md,
              }}
            >
              {newsList.map((item) => {
                const date = item.date?.seconds
                  ? new Date(item.date.seconds * 1000)
                  : null;
                const formattedDate = date
                  ? formatDateLocalized(date, { month: "short" })
                  : "";
                const formattedTime =
                  date?.toLocaleTimeString(getLocaleTag(), {
                    hour: "2-digit",
                    minute: "2-digit",
                  }) ?? "";
                return (
                  <AppCard key={item.id} accent="blue" padding={0}>
                    {/* Cabecera: icono comunicado + micro-label + fecha */}
                    <div
                      style={{
                        padding: "16px 16px 0",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                      }}
                    >
                      <IconBox
                        icon={<FaBullhorn />}
                        color={appColors.blue}
                        size={40}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            ...appType.statLabel,
                            color: appColors.blue,
                            marginBottom: 3,
                          }}
                        >
                          {t("announcement")}
                        </div>
                        <div
                          style={{
                            ...appType.cardTitle,
                            fontSize: 16,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {trTitle(item)}
                        </div>
                      </div>
                    </div>

                    {/* Cuerpo del comunicado */}
                    {(item.description || item.detail) && (
                      <div style={{ padding: "8px 16px 0" }}>
                        <p
                          style={{
                            ...appType.body,
                            color: appColors.ink2,
                            margin: 0,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {trBody(item)}
                        </p>
                      </div>
                    )}

                    {/* Footer estructurado: FECHA / HORA */}
                    {formattedDate && (
                      <div
                        style={{
                          margin: "14px 16px 16px",
                          paddingTop: 12,
                          borderTop: `1px solid ${appColors.line2}`,
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 12,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              ...appType.statLabel,
                              color: appColors.ink3,
                              marginBottom: 3,
                            }}
                          >
                            {t("date")}
                          </div>
                          <div
                            style={{
                              ...appType.body,
                              fontSize: 13.5,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {formattedDate}
                          </div>
                        </div>
                        {formattedTime && (
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                ...appType.statLabel,
                                color: appColors.ink3,
                                marginBottom: 3,
                              }}
                            >
                              {t("time")}
                            </div>
                            <div
                              style={{
                                ...appType.body,
                                fontSize: 13.5,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formattedTime}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </AppCard>
                );
              })}
            </div>
          </>
        )}
      </AppScreen>
    );
  }

  // ─── Versión desktop (existente) ────────────────────────────────────────────
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaBullhorn />
        </div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t("titleDesktop")}</h1>
          <p className={styles.subtitle}>{t("subtitleDesktop")}</p>
        </div>
      </div>

      <ComunicacionesResidente buildingId={selectedBuilding?.buildingid} />

      {!selectedBuilding?.buildingid ? (
        <div className={styles.emptyState}>
          <FaBullhorn className={styles.emptyIcon} />
          <p className={styles.noNews}>{t("selectBuildingDesktop")}</p>
        </div>
      ) : loading ? (
        <div className={styles.loaderContainer}><Loader /></div>
      ) : newsList.length === 0 ? (
        <div className={styles.emptyState}>
          <FaBullhorn className={styles.emptyIcon} />
          <p className={styles.noNews}>{t("emptyDesktop")}</p>
        </div>
      ) : (
        <div className={styles.feed}>
          {newsList.map((item) => {
            const date = item.date?.seconds ? new Date(item.date.seconds * 1000) : null;
            const formattedDate = date
              ? formatDateLocalized(date, { month: "short", includeTime: true })
              : "";
            return (
              <article key={item.id} className={styles.tweet}>
                <div className={styles.tweetAvatar}>
                  <FaBullhorn />
                </div>
                <div className={styles.tweetBody}>
                  <div className={styles.tweetMeta}>
                    <span className={styles.tweetAuthor}>{trTitle(item)}</span>
                    <span className={styles.tweetDot}>·</span>
                    <span className={styles.tweetDate}>{formattedDate}</span>
                  </div>
                  <p className={styles.tweetText}>{trBody(item)}</p>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default News;
